import { PaymentContext, PaymentMethod } from '@api/READ_ONLY/visits_api/Api';
import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import type { TFunction } from 'i18next';

export type VisitPaymentStatus = 'none' | 'pending' | 'unpaid' | 'paid';

type PillState = PaymentContext['pillState'];
/** A payment context as any backend sends it: one that predates pillState has none. */
export type VisitPaymentContext = Omit<PaymentContext, 'pillState'> & { pillState?: PillState | null };

// The backend's pill state, in this table's four words: due and partial are both "unpaid".
const FROM_PILL_STATE: Record<PillState, VisitPaymentStatus> = {
  paid: 'paid',
  due: 'unpaid',
  partial: 'unpaid',
  pending: 'pending',
  none: 'none',
};

/**
 * A visit's payment status. The backend decides it once (`pillState`, which mobile and the
 * visits list's payment filter read too); the rule below only runs for a backend that predates
 * it or a state this build does not know. Pure — no i18n.
 *
 * EVERY AMOUNT IN THE CONTEXT IS CYCLE-WIDE, so an owed state belongs to the visit
 * that CLOSES the cycle and to no other. A `collect_every_n = 14` request used to
 * print "€140 Pending" on all fourteen of its rows, which reads as fourteen
 * separate debts. `isCollectionVisit` is the backend's answer to "is it this
 * visit's turn"; mobile's derivePaymentState applies the same rule.
 *
 * 'paid' is deliberately NOT suppressed: it is settled money, it is the same
 * answer across the cycle, and hiding it would read as unpaid.
 */
export function getVisitPaymentStatus(ctx?: VisitPaymentContext | null): VisitPaymentStatus {
  if (!ctx) return 'none';
  const fromBackend = ctx.pillState ? FROM_PILL_STATE[ctx.pillState] : undefined;
  if (fromBackend) return fromBackend;
  // Planned money but nothing delivered yet: owes €0 so far, which is not "paid".
  if (Number(ctx.plannedAmount ?? 0) > 0 && Number(ctx.totalCharged ?? 0) <= 0) {
    return ctx.isCollectionVisit === false ? 'none' : 'pending';
  }
  if ((ctx.outstandingAmount ?? 0) <= 0) return 'paid';
  // Strict `=== false`: an absent flag is a backend that predates it, and keeping
  // the old (noisier) display beats hiding money that someone has to collect.
  if (ctx.isCollectionVisit === false) return 'none';
  return ctx.paymentRequired ? 'unpaid' : 'pending';
}

/** The planned period amount, when the period owes less because not every visit was completed. */
function plannedBeyondOwed(ctx: VisitPaymentContext | null | undefined): number | null {
  const planned = Number(ctx?.plannedAmount ?? 0);
  return planned > Number(ctx?.totalCharged ?? 0) ? planned : null;
}

function methodLabel(ctx: VisitPaymentContext | null | undefined, t: TFunction): string | null {
  switch (ctx?.defaultPaymentMethod) {
    case PaymentMethod.Cash:
      return t('service-requests-option-cash');
    case PaymentMethod.Card:
      return t('service-requests-option-card');
    default:
      return null;
  }
}

/**
 * Human-readable payment display for a visit.
 * compact  (list):  "Cash · €40.00 Unpaid" | "Cash · €40.00 Pending" | "Cash · Paid" | "-"
 * verbose  (form):  "Cash · €40.00 outstanding — Unpaid" | "Cash · €40.00 outstanding — Pending" | ...
 * When fewer visits were completed than planned the amount says so:
 *                   "Cash · €40.00 of €140.00 Unpaid" | "... (2 of 7 visits done) — Unpaid"
 */
export function formatVisitPayment(
  ctx: VisitPaymentContext | null | undefined,
  t: TFunction,
  opts?: { verbose?: boolean }
): string {
  const status = getVisitPaymentStatus(ctx);
  if (status === 'none') return '-';

  const parts: string[] = [];
  const method = methodLabel(ctx, t);
  if (method) parts.push(method);

  if (status === 'paid') {
    parts.push(t('visits-payment-paid'));
  } else {
    // 'unpaid' (due now) or 'pending' (accruing, not yet due).
    const statusLabel = status === 'unpaid' ? t('visits-payment-unpaid') : t('visits-payment-pending');
    // Hidden by the pricing middleware: the status alone, never a made-up €0.00.
    if (ctx?.outstandingAmount == null) {
      parts.push(statusLabel);
      return parts.join(' · ');
    }
    const planned = plannedBeyondOwed(ctx);
    const amount =
      currencyFormatter(ctx.outstandingAmount) +
      (planned !== null ? ` ${t('visits-payment-of-planned', { planned: currencyFormatter(planned) })}` : '');
    const visitsDone =
      planned !== null && ctx?.visitCount
        ? ` (${t('visits-payment-visits-done', { done: ctx.completedVisitCount ?? 0, count: ctx.visitCount })})`
        : '';
    parts.push(
      opts?.verbose
        ? `${amount} ${t('visits-payment-outstanding')}${visitsDone} — ${statusLabel}`
        : `${amount} ${statusLabel}`
    );
  }

  return parts.join(' · ');
}
